/**
 * Landing / sign-in page, one Bauhaus look whatever interface (Classic / Modern) the visitor chose:
 * the wordmark, one sentence and the "OverLyX = Overleaf + LyX …" line beside the sign-in card, the
 * demo clips (recorded in the Modern interface), four lines of what it does, the Overleaf import, a
 * footer. Google sign-in is the way in; the username + password form
 * exists for accounts an administrator created (seeded users, e2e) and stays folded away behind a
 * small link while Google is available.
 *
 * The demo clips (packages/client/public/landing/*) are real recordings of the editor, made by
 * scripts/recording/. Each exists in a light and a dark variant; the page shows the one matching
 * the visitor's theme. A clip plays once when scrolled into view and halts on its last frame with
 * a replay button.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Wordmark } from './Logo';
import { api, googleSignInUrl, type User } from '../api';
import { useTheme } from './theme';
import { OverleafStart } from './OverleafStart';
import './landing.css';

export const GITHUB_URL = 'https://github.com/japhba/overlyx';

export const GoogleG = () => (
  <svg class="g" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

/** The location hash, live (the Google links carry it as the place to return to). */
function useHash(): string {
  const [hash, setHash] = useState(location.hash);
  useEffect(() => {
    const on = () => setHash(location.hash);
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return hash;
}

/** Continue with Google — back to the document in the URL afterwards (a deep link, a guest keeping a project) */
const GoogleButton = () => (
  <a class="google" href={googleSignInUrl(useHash())} data-google-login><GoogleG /><span>Continue with Google</span></a>
);

/** the beta caveat: on the landing page's sign-in card and on the signed-in start page (Home.tsx) */
export const BetaNote = () => (
  <p class="beta-note" data-beta-note role="note"><strong>Beta:</strong> we can't guarantee server uptime or that no data is lost. Keep your own copy of important work.</p>
);

const VSCODE_URL = GITHUB_URL + '/releases/latest';

const VscodeIcon = () => (
  <svg class="g" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#007ACC" d="M23.15 2.587 18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z" />
  </svg>
);

interface Clip { name: string; title: string; note: string; extra?: ComponentChildren }
const CLIPS: Clip[] = [
  { name: 'wysiwyg', title: 'Text and formulas, as you write', note: 'Formulas render while you type. Check the PDF for the final page layout.' },
  { name: 'tex', title: 'A real .tex file underneath', note: 'Plain LaTeX, kept byte for byte. Edit the page or the source; they stay in sync.' },
  { name: 'slides', title: 'Slides and posters', note: 'Move and retype anything, edit formulas in place, present with a laser pointer.' },
  { name: 'drawing', title: 'Inkscape’s tools on the slide', note: 'Combine shapes, drag their nodes, draw curves or by hand.' },
  { name: 'collab', title: 'Writing together', note: 'Live cursors, comments in the margin, tracked changes.' },
  {
    name: 'vscode', title: 'The same editor in VS Code',
    note: 'Rendered .tex and a PDF preview, next to your files and git. ',
    extra: <a href={VSCODE_URL} target="_blank" rel="noopener">Get the extension.</a>,
  },
];

/** what it does, in four lines (the bar over each takes one of the wordmark's colours) */
const FEATURES: { title: string; text: string }[] = [
  { title: 'WYSIWYM LaTeX', text: 'What you see is what you mean: text and formulas render as you type. The file stays plain .tex, so it compiles anywhere.' },
  { title: 'Slides and posters', text: 'On a canvas, with the same formula editor and Inkscape’s drawing tools.' },
  { title: 'Together', text: 'Write live with others, comment and suggest changes, keep going offline.' },
  { title: 'Your tools', text: 'Git, your Overleaf projects and a VS Code extension. Keep your LaTeX preamble and packages; use the PDF for final layout.' },
];

/**
 * The demo gallery wheel: one clip at a time, auto-rotating — the active clip plays once, halts
 * on its last frame for a beat, then the wheel slides to the next (and wraps around). The
 * ". o .." dots show the position and jump to a clip; ↻ on the frame replays the current one.
 * Light or dark recordings are picked to match the visitor's theme.
 */
function DemoWheel() {
  const { theme } = useTheme();
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const videos = useRef<(HTMLVideoElement | null)[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const io = new IntersectionObserver(es => setInView(es.some(e => e.isIntersecting)), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // whenever the active clip, the visibility or the theme changes: everything else pauses, the
  // active clip starts over (scrolled away, the wheel stands still)
  useEffect(() => {
    clearTimeout(timer.current);
    videos.current.forEach((v, i) => { if (v && i !== active && !v.paused) v.pause(); });
    const v = videos.current[active];
    if (!v) return;
    if (inView) { v.currentTime = 0; void v.play().catch(() => {}); }
    else v.pause();
  }, [active, inView, theme]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onEnded = (i: number) => {
    if (i !== active) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setActive(a => (a + 1) % CLIPS.length), 1600);
  };
  const replay = (i: number) => {
    const v = videos.current[i];
    if (v) { clearTimeout(timer.current); v.currentTime = 0; void v.play().catch(() => {}); }
  };
  return (
    <div class="wheel" ref={wrap}>
      <div class="track" style={{ transform: `translateX(-${active * 100}%)` }}>
        {CLIPS.map((c, i) => {
          const src = `/landing/${c.name}-${theme}`;
          return (
            <figure class={'slide' + (i === active ? ' active' : '')} data-demo={c.name} aria-hidden={i !== active}>
              <div class="frame">
                {/* key: a theme flip swaps the sources, which <video> only picks up on a fresh element */}
                <video key={src} ref={el => { videos.current[i] = el; }} muted playsInline preload="metadata" poster={src + '.jpg'}
                  onEnded={() => onEnded(i)}>
                  <source src={src + '.webm'} type="video/webm" />
                  <source src={src + '.mp4'} type="video/mp4" />
                </video>
                <button type="button" class="replay" data-replay onClick={() => replay(i)} aria-label={'Replay: ' + c.title} tabIndex={i === active ? 0 : -1}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 5V2L7 6.5 12 11V7.5a5.5 5.5 0 1 1-5.5 5.5H4a8 8 0 1 0 8-8z" /></svg>
                  Replay
                </button>
              </div>
              <figcaption><strong>{c.title}</strong><span>{c.note}{c.extra}</span></figcaption>
            </figure>
          );
        })}
      </div>
      <div class="dots" role="tablist" aria-label="Demo clips">
        {CLIPS.map((c, i) => (
          <button type="button" role="tab" aria-selected={i === active} aria-label={c.title} data-dot={i}
            class={'dot' + (i === active ? ' active' : '')}
            onClick={() => { if (i === active) replay(i); else { clearTimeout(timer.current); setActive(i); } }} />
        ))}
      </div>
    </div>
  );
}

export function Login({ onLogin, google, note, onBack, backLabel = '← Continue as a guest for now' }: {
  onLogin: (u: User) => void; google: boolean;
  /** why the visitor is here (a share link that did not open, a guest keeping a project) */
  note?: string | null;
  /** a guest changed their mind: back to the document (or to the share link's invitation) */
  onBack?: () => void;
  backLabel?: string;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // the password form is the fallback: folded away while Google sign-in is offered
  const [wantPassword, setWantPassword] = useState(false);
  const showPassword = wantPassword || !google;
  // projects chosen in "Coming from Overleaf?" below: they follow the visitor through the sign-in
  const [pendingCount, setPendingCount] = useState(0);
  const scrollTo = (sel: string) => document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const importAndSignIn = () => {
    if (google) { location.href = googleSignInUrl(''); return; }
    setWantPassword(true);
    document.querySelector('.login')?.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => document.querySelector<HTMLInputElement>('.login input[placeholder="Username"]')?.focus(), 350);
  };
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try { const r = await api.login(username, password); onLogin(r.user); }
    catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(false); }
  };
  return (
    <div class="login lp">
      <div class="landing">
        <header class="hero">
          <div class="hero-copy">
            <h1><Wordmark bauhaus arrive /></h1>
            <p class="lede">LaTeX documents, slides and posters, edited and rendered in the same place.</p>
            {/* an align* block: the three lines line up at "=" */}
            <div class="formula" role="img" aria-label="OverLyX equals Overleaf plus LyX, plus Google Docs, Google Slides, Inkscape, VS Code and Markdown, minus complexity">
              <span class="lhs">OverLyX</span><span class="rel">=</span>
              <span class="row">Overleaf<i>+</i>LyX</span>
              <span class="row cont">(<i class="lead">+</i>Google Docs<i>+</i>Google Slides</span>
              <span class="row cont inner"><i class="lead">+</i>Inkscape<i>+</i>VS Code<i>+</i>Markdown)</span>
              <span class="row cont"><i class="lead">−</i>Complexity</span>
            </div>
          </div>
          <div class="signin-col">
          <form class="signin" onSubmit={submit}>
            <h2>Get started</h2>
            {note ? <p class="signin-note link-note" data-login-note>{note}</p>
              : pendingCount ? <p class="signin-note link-note" data-login-note>{pendingCount} Overleaf project{pendingCount === 1 ? '' : 's'} will be imported right after you sign in.</p>
              : <p class="signin-note">{google ? 'In the browser with your Google account, or in VS Code with the extension.' : 'In the browser with your account, or in VS Code with the extension.'}</p>}
            <BetaNote />
            {google && <GoogleButton />}
            <a class="vscode-get" data-vscode-get href={VSCODE_URL} target="_blank" rel="noopener">
              <VscodeIcon /><span>Get the VS Code extension</span>
            </a>
            {google && !showPassword && (
              <button type="button" class="fallback-link" data-password-login onClick={() => setWantPassword(true)}>
                I have a username and password
              </button>
            )}
            {showPassword && (
              <div class={'password' + (google ? ' fallback' : '')}>
                {google && <div class="fallback-note">Only for accounts an administrator created. With a Google account, use the button above.</div>}
                <input placeholder="Username" value={username} onInput={e => setUsername((e.target as HTMLInputElement).value)} autocomplete="username" autofocus />
                <input placeholder="Password" type="password" value={password} onInput={e => setPassword((e.target as HTMLInputElement).value)} autocomplete="current-password" />
                {err && <div class="err">{err}</div>}
                <button class={'btn' + (google ? '' : ' primary')} disabled={busy}>Sign in</button>
              </div>
            )}
            <p class="signin-note" data-terms-note>By signing in you agree to the <a href="/terms" target="_blank" rel="noopener">terms of use</a>. What we keep about you: <a href="/privacy" target="_blank" rel="noopener">privacy notice</a>.</p>
            {onBack && <button type="button" class="fallback-link" data-login-back onClick={onBack}>{backLabel}</button>}
            <a class="demos-hint" href="#overleaf" data-overleaf-hint onClick={e => { e.preventDefault(); scrollTo('#overleaf'); }}>Coming from Overleaf or LyX? Bring your projects along ↓</a>
          </form>
          </div>
        </header>
        <section class="demos sec" id="demos" aria-label="See it in action">
          <h2>See it in action</h2>
          <DemoWheel />
        </section>
        <section class="features sec" id="features" aria-label="What OverLyX does">
          <h2>What it does</h2>
          <ul class="flist">
            {FEATURES.map(f => <li><strong>{f.title}</strong><span>{f.text}</span></li>)}
          </ul>
        </section>
        <OverleafStart google={google} onSignIn={importAndSignIn} googleIcon={<GoogleG />} onChange={setPendingCount} />
        <footer class="foot">
          <span class="foot-name">OverLyX — open source, built on LyX and LaTeX.</span>
          <nav class="links" aria-label="About OverLyX">
            <a href={GITHUB_URL} target="_blank" rel="noopener">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path fill="currentColor" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" /></svg>
              GitHub
            </a>
            <a href={GITHUB_URL + '/issues/new'} target="_blank" rel="noopener">Report an issue</a>
            <a href="/privacy" data-privacy-link>Privacy</a>
            <a href="/terms" data-terms-link>Terms</a>
          </nav>
        </footer>
      </div>
    </div>
  );
}
